import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import {
  CreateConsentRequest,
  BulkCreateConsentRequest,
  UpdateConsentRequest,
  ConsentResponseSchema,
  ConsentType
} from './consent-api.models';

/**
 * Pure API service for user consent HTTP operations.
 * Maps to POST/GET/PATCH /v1/protected/consents
 *
 * No state management — just HTTP calls.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentApiService {
  private readonly logger = inject(LoggerService).scope('ConsentApiService');
  private readonly apiBaseUrl = `${environment.API_BASE_URL}/protected/consents`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * POST /consents — Save a single consent (creates or updates if same type exists)
   */
  createConsent(data: CreateConsentRequest): Observable<ConsentResponseSchema> {
    return this.http.post<ConsentResponseSchema>(this.apiBaseUrl, data).pipe(
      catchError(error => {
        this.logger.error('Error creating consent:', error);
        return throwError(() => new Error('Failed to save consent'));
      })
    );
  }

  /**
   * POST /consents/bulk — Save multiple consents at once
   */
  createBulkConsents(data: BulkCreateConsentRequest): Observable<ConsentResponseSchema[]> {
    return this.http.post<ConsentResponseSchema[]>(`${this.apiBaseUrl}/bulk`, data).pipe(
      catchError(error => {
        this.logger.error('Error creating bulk consents:', error);
        return throwError(() => new Error('Failed to save consents'));
      })
    );
  }

  /**
   * GET /consents — Get all consents for the current user
   */
  getConsents(): Observable<ConsentResponseSchema[]> {
    return this.http.get<ConsentResponseSchema[]>(this.apiBaseUrl).pipe(
      catchError(error => {
        this.logger.error('Error fetching consents:', error);
        return throwError(() => new Error('Failed to fetch consents'));
      })
    );
  }

  /**
   * PATCH /consents/{consent_type} — Update a specific consent by type
   */
  updateConsent(consentType: ConsentType, data: UpdateConsentRequest): Observable<ConsentResponseSchema> {
    return this.http.patch<ConsentResponseSchema>(
      `${this.apiBaseUrl}/${consentType}`,
      data
    ).pipe(
      catchError(error => {
        this.logger.error(`Error updating consent [${consentType}]:`, error);
        return throwError(() => new Error(`Failed to update consent: ${consentType}`));
      })
    );
  }
}
